'use client'

import React, { createContext, useContext, useMemo } from 'react'

import { Slot } from '@radix-ui/react-slot'
import { twMerge } from 'tailwind-merge'

// ============================================================================
// Context for Timeline Item Position
// ============================================================================

interface TimelineItemContext {
  isFirst: boolean
  isLast: boolean
}

const TimelineItemCtx = createContext<TimelineItemContext | null>(null)

function useTimelineItem() {
  const ctx = useContext(TimelineItemCtx)
  if (!ctx) {
    throw new Error(
      'Timeline.Item descendants must be used within Timeline.Item',
    )
  }
  return ctx
}

// ============================================================================
// Root Component
// ============================================================================

interface TimelineRootProps {
  children: React.ReactNode
  className?: string
  asChild?: boolean
}

/**
 * Timeline.Root - Container that provides position context to items
 *
 * Automatically determines first/last status for each Timeline.Item child.
 */
function TimelineRoot({ children, className, asChild }: TimelineRootProps) {
  const Comp = asChild ? Slot : 'div'

  // Count Timeline.Item children to provide position context
  const items = React.Children.toArray(children)
  const itemCount = items.length

  const enhancedChildren = items.map((child, index) => {
    if (!React.isValidElement(child)) return child

    // Wrap each child in position context
    return (
      <TimelineItemCtx.Provider
        key={child.key ?? index}
        value={{
          isFirst: index === 0,
          isLast: index === itemCount - 1,
        }}
      >
        {child}
      </TimelineItemCtx.Provider>
    )
  })

  return (
    <Comp className={twMerge('flex flex-col', className)}>
      {enhancedChildren}
    </Comp>
  )
}

TimelineRoot.displayName = 'Timeline.Root'

// ============================================================================
// Item Component (provides layout with connecting lines)
// ============================================================================

interface TimelineItemProps {
  children: React.ReactNode
  className?: string
  /**
   * Pixel offset from top where the dot center is located.
   * Lines connect at this point. Default: 8
   */
  dotCenterY?: number
  /**
   * Pixel offset from left where the line is drawn.
   * Should be center of the dot. Default: 5
   */
  lineX?: number
  /**
   * Line width in pixels. Default: 2
   */
  lineWidth?: number
  /**
   * Line color class. Default: 'bg-primary-200 dark:bg-primary-700'
   */
  lineColor?: string
  asChild?: boolean
}

/**
 * Timeline.Item - Individual timeline entry with connecting lines
 *
 * Uses CSS custom properties for dynamic positioning of the connecting
 * lines. The before/after pseudo-elements read these values via inline
 * styles since Tailwind JIT can't process dynamically constructed classes.
 */
function TimelineItem({
  children,
  className,
  dotCenterY = 8,
  lineX = 5,
  lineWidth = 2,
  lineColor = 'bg-primary-200 dark:bg-primary-700',
  asChild,
}: TimelineItemProps) {
  const { isFirst, isLast } = useTimelineItem()
  const Comp = asChild ? Slot : 'div'

  // Use CSS custom properties for dynamic values
  const style = useMemo(
    () =>
      ({
        '--timeline-line-x': `${lineX}px`,
        '--timeline-dot-center': `${dotCenterY}px`,
        '--timeline-line-width': `${lineWidth}px`,
      }) as React.CSSProperties,
    [lineX, dotCenterY, lineWidth],
  )

  // Map lineColor to before:/after: prefixed classes
  const lineColorClasses = useMemo(() => {
    const colors = lineColor.split(' ')
    return {
      before: colors.map((c) => `before:${c}`).join(' '),
      after: colors.map((c) => `after:${c}`).join(' '),
    }
  }, [lineColor])

  return (
    <Comp
      style={style}
      className={twMerge(
        'relative',
        isLast ? 'pb-0' : 'pb-4',
        // Line from top to dot-center
        'before:absolute before:top-0 before:left-[var(--timeline-line-x)] before:h-[var(--timeline-dot-center)] before:w-[var(--timeline-line-width)]',
        lineColorClasses.before,
        isFirst && 'before:hidden',
        // Line from dot-center to bottom
        'after:absolute after:bottom-0 after:left-[var(--timeline-line-x)] after:top-[var(--timeline-dot-center)] after:w-[var(--timeline-line-width)]',
        lineColorClasses.after,
        isLast && 'after:hidden',
        className,
      )}
    >
      {children}
    </Comp>
  )
}

TimelineItem.displayName = 'Timeline.Item'

// ============================================================================
// Dot Component (renders above the line)
// ============================================================================

interface TimelineDotProps {
  children?: React.ReactNode
  className?: string
  asChild?: boolean
}

/**
 * Timeline.Dot - Visual marker on the timeline
 *
 * Positioned absolutely with z-index to render above connecting lines.
 * Can contain custom content or use default circular dot styling.
 */
function TimelineDot({ children, className, asChild }: TimelineDotProps) {
  const Comp = asChild ? Slot : 'div'

  return (
    <Comp className={twMerge('absolute z-10 rounded-full', className)}>
      {children}
    </Comp>
  )
}

TimelineDot.displayName = 'Timeline.Dot'

// ============================================================================
// Content Component
// ============================================================================

interface TimelineContentProps {
  children: React.ReactNode
  className?: string
  asChild?: boolean
}

/**
 * Timeline.Content - Container for timeline entry content
 *
 * Positioned with left padding to accommodate the dot and line.
 */
function TimelineContent({
  children,
  className,
  asChild,
}: TimelineContentProps) {
  const Comp = asChild ? Slot : 'div'

  return <Comp className={twMerge('flex flex-col', className)}>{children}</Comp>
}

TimelineContent.displayName = 'Timeline.Content'

// ============================================================================
// Composed Export
// ============================================================================

export const Timeline = {
  Root: TimelineRoot,
  Item: TimelineItem,
  Dot: TimelineDot,
  Content: TimelineContent,
}

export type {
  TimelineRootProps,
  TimelineItemProps,
  TimelineDotProps,
  TimelineContentProps,
}

// Re-export context hook for advanced usage
export { useTimelineItem }
